import { memo, useEffect, useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import SelectField from 'app/shared-components/Forms/Selects/SelectField';
import { mapArrayToSelectFieldData } from '@/utils/formatters';
import { useCompanySearch } from '@/app/hooks/useCompanySearch';
import {
  Autocomplete,
  Box,
  Checkbox,
  FormControlLabel,
  TextField,
  Typography,
} from '@mui/material';
import WYSIWYGEditor from 'app/shared-components/WYSIWYGEditor';
import { useLazyGetCompaniesByIdsQuery } from '@/app/api/companiesApiSlice';
import { Company } from '../../Types/Company';
import { useGlobalData } from '@/app/providers/GlobalDataProvider';
import { useRoleCheck } from '@/app/hooks/useRoleCheck';

const userInformation: { id: string; fieldName: string }[] = [
  {
    id: 'maxLeeds',
    fieldName: 'Max Leads',
  },
  {
    id: 'payPercentage',
    fieldName: 'Pay Percentage',
  },
];

const salesLevelOptions = ['1', '2', '3', '4', '5'];
const salesLevelsData = mapArrayToSelectFieldData(salesLevelOptions);

const AddUserRoleSpecificForm = () => {
  const {
    control,
    formState: { errors },
    setValue,
    getValues,
  } = useFormContext();
  const { roles } = useGlobalData();
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);

  const { searchTerm, setSearchTerm, searchResults, debouncedSearch, isLoading } =
    useCompanySearch();
  const [triggerCompaniesFetch] = useLazyGetCompaniesByIdsQuery();

  useEffect(() => {
    if (getValues('referralCompanyId') == null) {
      return;
    }

    const fetchCompanies = async () => {
      const { data } = await triggerCompaniesFetch(getValues('referralCompanyId'));
      if (data) {
        setSelectedCompany(data.items[0]);
        setValue('referralCompanyId', data.items[0].id);
      }
    };
    if (searchResults.size === 0) {
      fetchCompanies();
    }
  }, [searchResults, triggerCompaniesFetch]);

  const watchedRoleId = useWatch({ control, name: 'roleId' });
  const name = roles?.items.find((role) => role.id === watchedRoleId)?.name;

  const isReq = name === 'Referral Office' || name === 'Company Owner';

  const isSuperAdmin = useRoleCheck(['Super Admin']);
  const visibleUserInformation = userInformation.filter(
    (i) => i.id !== 'payPercentage' || isSuperAdmin,
  );

  return (
    <Box className='flex flex-col items-center justify-center w-full py-8'>
      <Typography variant='h4' className='pt-32' component='h4'>
        Role-specific Fields
      </Typography>

      <Controller
        name='referralCompanyId'
        control={control}
        defaultValue={null}
        render={() => (
          <Autocomplete
            value={selectedCompany}
            className='w-full py-8'
            id='referralCompanyId'
            noOptionsText={
              searchResults.get(searchTerm)?.items ? 'No options' : 'Type to search...'
            }
            loading={isLoading}
            options={searchResults.get(searchTerm)?.items ?? []}
            getOptionLabel={(option) => {
              const isString = typeof option === 'string';
              const returnVal = isString ? option : option.name;
              return returnVal;
            }}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            filterOptions={(x) => x}
            onChange={(_e, selectedVal) => {
              const newCompany = selectedVal as Company | null;
              setSelectedCompany(newCompany);
              setValue('referralCompanyId', newCompany?.id ?? null);
            }}
            onInputChange={(_, value, reason) => {
              if (reason === 'input') {
                setSearchTerm(value ?? '');
                debouncedSearch(value);
              }
              if (reason === 'clear') {
                setSearchTerm('');
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                required={isReq}
                label='Referral Company'
                error={!!errors['referralCompanyId']}
                variant='outlined'
                size='small'
                className='w-full'
                value={searchTerm}
                placeholder='Type to search...'
              />
            )}
          />
        )}
      />
      <SelectField name='salesLevel' label='Sales Level' data={salesLevelsData} />
      {visibleUserInformation.map(({ id, fieldName }) => {
        return (
          <Box className='flex flex-row items-center justify-center w-full py-8' key={id}>
            <Controller
              name={id}
              control={control}
              defaultValue=''
              render={({ field }) => {
                const err = errors[id]?.message as string;
                return (
                  <TextField
                    {...field}
                    label={fieldName}
                    size='small'
                    variant='outlined'
                    className='w-full'
                    error={!!errors[id]}
                    helperText={err}
                  />
                );
              }}
            />
          </Box>
        );
      })}
      <Box className='flex flex-row items-center justify-start w-full py-8'>
        <Controller
          name='skip'
          control={control}
          render={({ field }) => (
            <FormControlLabel
              control={<Checkbox {...field} id='skip' size='small' />}
              label='Skip'
            />
          )}
        />
        <Controller
          name='dripActive'
          control={control}
          render={({ field }) => (
            <FormControlLabel
              control={<Checkbox {...field} id='dripActive' size='small' />}
              label='Drip Active'
            />
          )}
        />
      </Box>

      <Controller
        name='details.description'
        control={control}
        render={({ field }) => {
          return <WYSIWYGEditor {...field} className='' defaultValue={field.value} />;
        }}
      />
    </Box>
  );
};

export default memo(AddUserRoleSpecificForm);
